import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Collapse,
  Card,
  CardContent,
  CardActions,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Hotel as HotelIcon,
  Home as AirbnbIcon,
  Apartment as ApartmentIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Accommodation } from '../../types/Trip';
import { v4 as uuidv4 } from 'uuid';

// Icons for different accommodation types
const accommodationIcons: Record<string, React.ReactElement> = {
  hotel: <HotelIcon />,
  airbnb: <AirbnbIcon />,
  hostel: <HotelIcon sx={{ transform: 'rotate(90deg)' }} />,
  apartment: <ApartmentIcon />,
  other: <LocationIcon />
};

interface AccommodationFormProps {
  accommodations: Accommodation[];
  onChange: (accommodations: Accommodation[]) => void;
  tripStartDate?: Date | string | null;
  tripEndDate?: Date | string | null;
}

const AccommodationForm: React.FC<AccommodationFormProps> = ({
  accommodations,
  onChange,
  tripStartDate,
  tripEndDate
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [newAccommodation, setNewAccommodation] = useState<Accommodation>({
    id: '',
    name: '',
    address: '',
    coordinates: [0, 0],
    type: 'hotel',
    checkIn: tripStartDate ? new Date(tripStartDate).toISOString() : new Date().toISOString(),
    checkOut: tripEndDate ? new Date(tripEndDate).toISOString() : new Date().toISOString(),
    notes: '',
    price: 0,
    currency: 'USD'
  });

  const handleAddNewAccommodation = () => {
    // Create a new accommodation with a unique ID
    const accommodationToAdd = {
      ...newAccommodation,
      id: uuidv4()
    };

    // Add to the list
    const updatedAccommodations = [...accommodations, accommodationToAdd];
    onChange(updatedAccommodations);

    // Reset the form
    setNewAccommodation({
      id: '',
      name: '',
      address: '',
      coordinates: [0, 0],
      type: 'hotel',
      checkIn: tripStartDate ? new Date(tripStartDate).toISOString() : new Date().toISOString(),
      checkOut: tripEndDate ? new Date(tripEndDate).toISOString() : new Date().toISOString(),
      notes: '',
      price: 0,
      currency: 'USD'
    });

    // Collapse the form
    setExpanded(false);
  };

  const handleUpdateAccommodation = () => {
    if (editIndex === null) return;

    // Update the accommodation at the specified index
    const updatedAccommodations = [...accommodations];
    updatedAccommodations[editIndex] = newAccommodation;
    onChange(updatedAccommodations);

    // Reset the form
    setEditIndex(null);
    setNewAccommodation({
      id: '',
      name: '',
      address: '',
      coordinates: [0, 0],
      type: 'hotel',
      checkIn: tripStartDate ? new Date(tripStartDate).toISOString() : new Date().toISOString(),
      checkOut: tripEndDate ? new Date(tripEndDate).toISOString() : new Date().toISOString(),
      notes: '',
      price: 0,
      currency: 'USD'
    });

    // Collapse the form
    setExpanded(false);
  };

  const handleEditAccommodation = (index: number) => {
    setEditIndex(index);
    setNewAccommodation(accommodations[index]);
    setExpanded(true);
  };

  const handleDeleteAccommodation = (index: number) => {
    const updatedAccommodations = [...accommodations];
    updatedAccommodations.splice(index, 1);
    onChange(updatedAccommodations);
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setNewAccommodation({
      id: '',
      name: '',
      address: '',
      coordinates: [0, 0],
      type: 'hotel',
      checkIn: tripStartDate ? new Date(tripStartDate).toISOString() : new Date().toISOString(),
      checkOut: tripEndDate ? new Date(tripEndDate).toISOString() : new Date().toISOString(),
      notes: '',
      price: 0,
      currency: 'USD'
    });
    setExpanded(false);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="div">
          Accommodations
        </Typography>
        <Button
          startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setExpanded(!expanded)}
          color="primary"
        >
          {expanded ? 'Hide Form' : 'Add Accommodation'}
        </Button>
      </Box>

      <Collapse in={expanded}>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {editIndex !== null ? 'Edit Accommodation' : 'Add New Accommodation'}
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Accommodation Name"
                  fullWidth
                  margin="normal"
                  value={newAccommodation.name}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Type"
                  fullWidth
                  margin="normal"
                  value={newAccommodation.type}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, type: e.target.value as any })}
                  required
                >
                  <MenuItem value="hotel">Hotel</MenuItem>
                  <MenuItem value="airbnb">Airbnb</MenuItem>
                  <MenuItem value="hostel">Hostel</MenuItem>
                  <MenuItem value="apartment">Apartment</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  fullWidth
                  margin="normal"
                  value={newAccommodation.address}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, address: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker 
                  label="Check-in Date"
                  value={newAccommodation.checkIn ? new Date(newAccommodation.checkIn) : null}
                  onChange={(date) => setNewAccommodation({
                    ...newAccommodation,
                    checkIn: date ? (date as Date).toISOString() : undefined
                  })}
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth />
                  )}
                 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker 
                  label="Check-out Date"
                  value={newAccommodation.checkOut ? new Date(newAccommodation.checkOut) : null}
                  onChange={(date) => setNewAccommodation({
                    ...newAccommodation,
                    checkOut: date ? (date as Date).toISOString() : undefined
                  })}
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  label="Price per Night"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={newAccommodation.price || ''}
                  onChange={(e) => setNewAccommodation({ 
                    ...newAccommodation, 
                    price: parseFloat(e.target.value) || 0 
                  })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  select
                  label="Currency"
                  fullWidth
                  margin="normal"
                  value={newAccommodation.currency || 'USD'}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, currency: e.target.value })}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="CAD">CAD</MenuItem>
                  <MenuItem value="JPY">JPY</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Photo URL (optional)"
                  fullWidth
                  margin="normal"
                  value={newAccommodation.photoUrl || ''}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, photoUrl: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes (optional)"
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                  value={newAccommodation.notes || ''}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, notes: e.target.value })}
                  placeholder="Add any additional information about your accommodation"
                />
              </Grid>
            </Grid>
          </LocalizationProvider>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            <Button variant="outlined" color="inherit" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={editIndex !== null ? handleUpdateAccommodation : handleAddNewAccommodation}
              disabled={!newAccommodation.name || !newAccommodation.address}
            >
              {editIndex !== null ? 'Update' : 'Add'}
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* List of added accommodations */}
      {accommodations.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Added Accommodations ({accommodations.length})
          </Typography>
          <Grid container spacing={2}>
            {accommodations.map((accommodation, index) => (
              <Grid item xs={12} sm={6} key={accommodation.id || index}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ mr: 1, color: 'primary.main' }}>
                        {accommodation.type && accommodationIcons[accommodation.type] || <LocationIcon />}
                      </Box>
                      <Typography variant="h6" component="div">
                        {accommodation.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {accommodation.address}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Check-in:
                      </Typography>
                      <Typography variant="body2">
                        {accommodation.checkIn 
                          ? new Date(accommodation.checkIn).toLocaleDateString() 
                          : 'Not specified'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Check-out:
                      </Typography>
                      <Typography variant="body2">
                        {accommodation.checkOut 
                          ? new Date(accommodation.checkOut).toLocaleDateString() 
                          : 'Not specified'}
                      </Typography>
                    </Box>
                    {accommodation.price && accommodation.price > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">
                          Price:
                        </Typography>
                        <Typography variant="body2">
                          {accommodation.price} {accommodation.currency}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <IconButton size="small" onClick={() => handleEditAccommodation(index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteAccommodation(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default AccommodationForm; 